import React from 'react';

import './styles.css';

import logo from '../../../assets/img/logo.webp';

import { useLocation, useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { trackMatomoEvent } from '../../../utils/matomo';

const LinkedinIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
  </svg>
);

const InstagramIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M16.98 0H7.02C3.15 0 0 3.15 0 7.02v9.96C0 20.85 3.15 24 7.02 24h9.96C20.85 24 24 20.85 24 16.98V7.02C24 3.15 20.85 0 16.98 0zm4.68 16.98a4.69 4.69 0 0 1-4.68 4.68H7.02a4.69 4.69 0 0 1-4.68-4.68V7.02a4.69 4.69 0 0 1 4.68-4.68h9.96a4.69 4.69 0 0 1 4.68 4.68v9.96z" />

    <path d="M12 5.84A6.16 6.16 0 1 0 18.16 12 6.17 6.17 0 0 0 12 5.84zm0 10.16A4 4 0 1 1 16 12a4 4 0 0 1-4 4z" />

    <circle cx="18.4" cy="5.6" r="1.44" />
  </svg>
);

const Footer = () => {
  const { t } = useTranslation();

  const location = useLocation();

  const navigate = useNavigate();

  const footerBgColor = '#004A7F';

  const isHome = location.pathname === '/';

  const handleScrollOrNavigate =
    (id: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      trackMatomoEvent('Navigation', 'click', `Footer ${id}`);

      if (isHome) {
        const section = document.getElementById(id);

        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate('/', { state: { scrollToId: id } });
      }
    };

  return (
    <footer className="footer-wrapper">
      <div className="footer-wave-container">
        <svg
          className="footer-wave-svg"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient
              id="footer-wave-gradient"
              x1="0.5"
              y1="0"
              x2="0.5"
              y2="1"
            >
              <stop offset="0%" stopColor={footerBgColor} stopOpacity="0.3" />

              <stop offset="100%" stopColor={footerBgColor} stopOpacity="1.6" />
            </linearGradient>
          </defs>

          <path
            d="M0,192L48,197.3C96,203,192,213,288,208C384,203,480,181,576,176C672,171,768,181,864,197.3C960,213,1056,235,1152,240C1248,245,1344,235,1392,229.3L1440,224L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            fill="url(#footer-wave-gradient)"
          ></path>
        </svg>
      </div>

      <div
        className="footer-content"
        style={{ backgroundColor: footerBgColor }}
      >
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-logo-section">
              <div className="logo-container">
                <img
                  src={logo}
                  alt="BlueAlps Logo"
                  fetchPriority="low"
                  decoding="async"
                  loading="lazy"
                />

                <div>
                  <h2 className="logo-title">BLUEALPS</h2>

                  <span className="logo-subtitle">RESPONSIBLE IT</span>
                </div>
              </div>

              <div className="social-links">
                <a
                  href="https://www.linkedin.com/company/bluealps/"
                  aria-label="LinkedIn"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackMatomoEvent('Social', 'click', 'LinkedIn')
                  }
                >
                  <LinkedinIcon className="social-icon" />
                </a>

                <a
                  href="https://www.instagram.com/bluealpsorg/"
                  aria-label="Instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackMatomoEvent('Social', 'click', 'Instagram')
                  }
                >
                  <InstagramIcon className="social-icon" />
                </a>
              </div>
            </div>

            <div className="footer-links-section">
              <p className="footer-heading">{t('footer.extra_links')}</p>

              <ul className="footer-links-list">
                <li>
                  <a
                    href="/"
                    onClick={() =>
                      trackMatomoEvent('Navigation', 'click', 'Footer Home')
                    }
                  >
                    {t('footer.home')}
                  </a>
                </li>

                <li>
                  <a href="/#about" onClick={handleScrollOrNavigate('about')}>
                    {t('footer.about')}
                  </a>
                </li>

                <li>
                  <a
                    href="/services"
                    onClick={() =>
                      trackMatomoEvent('Navigation', 'click', 'Footer Services')
                    }
                  >
                    {t('footer.services')}
                  </a>
                </li>

                <li>
                  <a
                    href="/projects"
                    onClick={() =>
                      trackMatomoEvent('Navigation', 'click', 'Footer Projects')
                    }
                  >
                    {t('footer.project')}
                  </a>
                </li>

                <li>
                  <a
                    href="/impact"
                    onClick={() =>
                      trackMatomoEvent('Navigation', 'click', 'Footer Impact')
                    }
                  >
                    {t('footer.impact')}
                  </a>
                </li>
              </ul>
            </div>

            <div className="footer-contact-section">
              <a
                href="/contact"
                className="contact-button"
                onClick={() =>
                  trackMatomoEvent('CTA', 'click', 'Footer contact')
                }
              >
                {t('footer.contact_button')}
              </a>
            </div>
          </div>
        </div>

        <div className="footer-links-policy-list">
          <a
            href="/imprint"
            onClick={() =>
              trackMatomoEvent('Navigation', 'click', 'Footer Imprint')
            }
          >
            {t('footer.imprint')}
          </a>

          <a
            href="/privacy-policy"
            onClick={() =>
              trackMatomoEvent('Navigation', 'click', 'Footer Privacy Policy')
            }
          >
            {t('footer.privacy')}
          </a>

          <a
            href="/cookie-policy"
            onClick={() =>
              trackMatomoEvent('Navigation', 'click', 'Footer Cookie Policy')
            }
          >
            {t('footer.cookie')}
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
